<script setup lang="ts">
import { useRouter } from "vue-router";
//import Lucide from "../../base-components/Lucide";
import { FormattedMenu, linkTo } from "./main-menu";
import {checkTypeOfIcon, substractNameOfIcon} from "@wpmss/ts-icons-helper"

interface MenuProps {
  class?: string | object;
  menu: FormattedMenu;
  isRoot: boolean;
  layout: "rubick" | "enigma" | "icewall" | "tinker";
}

const router = useRouter();
// @ts-ignore
const props = defineProps<MenuProps>();
</script>

<template>
  <a
    :key="props.menu.title"
    :href="
      props.menu.subMenu && props.menu.subMenu.length > 0
        ? 'javascript:;'
        : 'top-menu/management/' + props.menu.href
    "
    :class="[
      {
        'h-[55px] rounded-full xl:rounded-b-none xl:rounded-t-[1rem] flex items-center text-white relative':
          layout === 'rubick',
        'px-5 mr-1': isRoot && layout === 'rubick',
        'px-0 mr-0': !isRoot && layout === 'rubick',
        'z-10 bg-slate-100 dark:bg-darkmode-700':
          isRoot && props.menu.active && layout === 'rubick',

        'before:content-[\'\'] before:w-[20px] before:h-[20px] before:-ml-[20px] before:rotate-90 before:scale-[1.04] before:bg-[length:100%] before:bg-menu-corner before:absolute before:bottom-0 before:left-0 before:hidden before:xl:block dark:before:bg-menu-corner-dark':
          isRoot && props.menu.active && layout === 'rubick',
        'after:content-[\'\'] after:w-[20px] after:h-[20px] after:-mr-[20px] after:rotate-180 after:scale-[1.04] after:bg-[length:100%] after:bg-menu-corner after:absolute after:bottom-0 after:right-0 after:hidden after:xl:block dark:after:bg-menu-corner-dark':
          isRoot && props.menu.active && layout === 'rubick',
      },
      {
        'h-[55px] flex items-center px-5 mr-1 text-slate-600 relative rounded-full xl:rounded-xl':
          layout === 'enigma',
        'mt-[3px]': isRoot && layout === 'enigma',
        'bg-slate-100 text-primary dark:bg-darkmode-700':
          isRoot && props.menu.active && layout === 'enigma',
        'before:content-[\'\'] before:hidden xl:before:block before:inset-0 before:rounded-xl before:absolute before:border-b-[3px] before:border-solid before:border-black/[0.08] dark:before:border-black/[0.08]':
          isRoot && props.menu.active && layout === 'enigma',
        'after:content-[\'\'] after:w-[20px] after:h-[80px] after:bg-menu-active after:bg-no-repeat after:bg-cover after:absolute after:left-0 after:right-0 after:bottom-0 after:mx-auto after:transform after:rotate-90 after:hidden xl:after:block after:dark:bg-menu-active-dark':
          isRoot && props.menu.active && layout === 'enigma',
        'px-0 mr-0': !isRoot && layout === 'enigma',

        // Animation
        'after:opacity-0 after:-mb-[74px] after:animate-[0.3s_ease-in-out_1s_active-top-menu-chevron] after:animate-fill-mode-forwards':
          isRoot && props.menu.active && layout === 'enigma',
      },
      {
        'h-[55px] xl:h-[47px] flex items-center px-5 mr-1 text-white relative rounded-full xl:rounded-lg':
          layout === 'icewall',
        'px-0 mr-0': !isRoot && layout === 'icewall',
        'mt-[3px]': isRoot && layout === 'icewall',
        'bg-slate-100 dark:bg-darkmode-700 xl:bg-primary':
          isRoot && props.menu.active && layout === 'icewall',
        'before:content-[\'\'] before:hidden xl:before:block before:inset-0 before:bg-white/[0.08] before:rounded-lg before:absolute before:border-b-[3px] before:border-solid before:border-black/10 before:dark:border-black/10':
          isRoot && props.menu.active && layout === 'icewall',
        'after:content-[\'\'] after:animate-[0.3s_ease-in-out_1s_active-top-menu-chevron] after:animate-fill-mode-forwards after:hidden xl:after:block after:w-[20px] after:h-[80px] after:-mb-[74px] after:bg-menu-active after:bg-no-repeat after:bg-cover after:absolute after:left-0 after:right-0 after:bottom-0 after:mx-auto after:transform after:rotate-90 after:opacity-0 dark:after:bg-menu-active-dark':
          isRoot && props.menu.active && layout === 'icewall',
      },
      {
        'h-[55px] flex items-center px-5 mr-1 text-white relative rounded-full xl:rounded-xl':
          layout === 'tinker',
        'mt-[3px]': isRoot && layout === 'tinker',
        'bg-slate-100 xl:bg-primary dark:bg-darkmode-700':
          isRoot && props.menu.active && layout === 'tinker',
        'before:content-[\'\'] before:hidden xl:before:block before:inset-0 before:bg-white/[0.08] before:rounded-xl before:absolute before:border-b-[3px] before:border-solid before:border-black/10 before:dark:border-black/10 before:dark:bg-darkmode-700':
          isRoot && props.menu.active && layout === 'tinker',
        'px-0 mr-0': !isRoot && layout === 'tinker',
      },
      props.class,
    ]"
    @click="linkTo(props.menu, router, $event, props.layout)"
  >
    <div
      :class="[
        {
          'dark:text-slate-400': layout === 'rubick',
          'text-primary dark:text-white':
            isRoot && props.menu.active && layout === 'rubick',

          'before:content-[\'\'] before:z-[-1] before:absolute before:w-full before:h-full before:top-0 before:left-0 before:rounded-full xl:before:rounded-b-none xl:before:rounded-t-lg before:transition before:ease-in before:duration-100':
            !props.menu.active && layout === 'rubick',
        },
        {
          'z-10 dark:text-slate-400': layout === 'enigma',
          'dark:text-white': isRoot && props.menu.active && layout === 'enigma',
        },
        {
          'z-10 dark:text-slate-400': layout === 'icewall',
          'text-black xl:text-white dark:text-white':
            isRoot && props.menu.active && layout === 'icewall',
        },
        {
          'z-10 dark:text-slate-400': layout === 'tinker',
          '-mt-[3px]': isRoot && layout === 'tinker',
          'dark:text-white text-primary xl:text-white':
            isRoot && props.menu.active && layout === 'tinker',
        },
      ]"
    >
      <!--Menu icon here-->
      <div v-if="props.menu.menu_icon != 'no icon'">
      <font-awesome-icon class="h-7"
        v-if="checkTypeOfIcon(props.menu.rec_menu_icon_parent) == 'FontAwesome'"
        :icon="substractNameOfIcon(props.menu.menu_icon)"
      />
      
      <Lucide  v-if="checkTypeOfIcon(props.menu.rec_menu_icon_parent) == 'Lucide'" :icon="props.menu.menu_icon" class="" />
      <component v-if="checkTypeOfIcon(props.menu.rec_menu_icon_parent) == 'HeroIcon'" :is="props.menu.menu_icon" class="w-9" />
      </div>
    </div>
    <div
      :class="[
        {
          'ml-3 flex items-center whitespace-nowrap dark:text-slate-400':
            layout !== 'tinker',
          'text-black font-medium dark:text-white':
            isRoot &&
            props.menu.active &&
            (layout === 'rubick' || layout === 'enigma'),
          'w-full': !isRoot && layout !== 'tinker',
        },
        {
          'text-black xl:text-white font-medium dark:text-white':
            isRoot && props.menu.active && layout === 'icewall',
        },
        {
          'ml-3 flex items-center whitespace-nowrap z-10 dark:text-slate-400':
            layout === 'tinker',
          '-mt-[3px]': isRoot && layout === 'tinker',
          'font-medium dark:text-white text-slate-800 xl:text-white':
            isRoot && props.menu.active && layout === 'tinker',
          'w-full': !isRoot && layout === 'tinker',
        },
      ]"
    >
      {{ $t(props.menu.title) }}
      <Lucide
        v-if="props.menu.subMenu && props.menu.subMenu.length > 0"
        icon="ChevronDown"
        :class="[
          'hidden w-4 h-4 transition duration-100 ease-in xl:block',
          { 'ml-2': isRoot },
          { 'ml-auto': !isRoot },
        ]"
      />
    </div>
  </a>
</template>
